import React, { useMemo } from "react";
import Icon, { IconName, IconTile } from "./Icon";
import { AVATARS, AVATAR_TONES, SPOON_LABELS, TileTone } from "../profileOptions";
import { AvatarConfig, normalizeAvatar, renderAvatar } from "../avatars";

const tileTones: Record<TileTone, string> = {
  pink: "icon-tile-pink",
  blue: "icon-tile-blue",
  gold: "icon-tile-gold",
  green: "icon-tile-green",
  violet: "icon-tile-violet",
};

// Draws a 16x16 pixel character as crisp SVG, one rect per run of color.
export function PixelArt({ config, size }: { config: AvatarConfig; size: number }) {
  const rects = useMemo(() => {
    const grid = renderAvatar(config);
    const out: { x: number; y: number; w: number; fill: string }[] = [];
    grid.forEach((row, y) => {
      let x = 0;
      while (x < 16) {
        const fill = row[x];
        if (!fill) {
          x++;
          continue;
        }
        let w = 1;
        while (x + w < 16 && row[x + w] === fill) w++;
        out.push({ x, y, w, fill });
        x += w;
      }
    });
    return out;
  }, [config]);

  return (
    <svg
      viewBox="0 0 16 16"
      width={size}
      height={size}
      shapeRendering="crispEdges"
      aria-hidden="true"
      focusable="false"
    >
      {rects.map((r, i) => (
        <rect key={i} x={r.x} y={r.y} width={r.w} height={1} fill={r.fill} />
      ))}
    </svg>
  );
}

// The person's avatar on a glossy tile: their pixel character if they've
// made one, an older icon avatar if they picked one, or a default friend.
export function Avatar({
  config,
  icon,
  tone,
  size = 56,
}: {
  config?: unknown;
  icon?: string;
  tone?: string;
  size?: number;
}) {
  const color: TileTone = AVATAR_TONES.includes(tone as TileTone)
    ? (tone as TileTone)
    : "pink";

  if (!config && icon && AVATARS.some((a) => a.icon === icon)) {
    return <IconTile name={icon as IconName} tone={color} size={size} />;
  }

  return (
    <span
      className={`icon-tile avatar-tile ${tileTones[color]}`}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <PixelArt config={normalizeAvatar(config)} size={Math.round(size * 0.84)} />
    </span>
  );
}

// Read-only spoon meter, e.g. ●●●○○ but with little spoons.
export function SpoonMeter({ spoons }: { spoons?: number }) {
  if (!spoons) return null;
  return (
    <span className="spoon-meter" role="img" aria-label={`${spoons} of 5 spoons: ${SPOON_LABELS[spoons]}`}>
      <span className="spoon-row" aria-hidden="true">
        {[1, 2, 3, 4, 5].map((n) => (
          <Icon
            key={n}
            name="spoon"
            size={24}
            tone={n <= spoons ? "pink" : "ink"}
            className={n <= spoons ? "" : "spoon-empty"}
          />
        ))}
      </span>
      <span className="spoon-label">{SPOON_LABELS[spoons]}</span>
    </span>
  );
}
